import { makeAutoObservable, runInAction } from "mobx";
import { CartDetailReqParams, CartDetails } from "../models/CartItems";
import agent from "../api/agent";
import { AddToCartReqParams, AddToCartRes, ClearCartReqParams, ExcelUploadResponse } from "../models/AddedCartItems";
import { VerifyCouponApiResponse } from "../models/Coupons";
import { GetAllDeliverySlotsApiResponse } from "../models/DeliverySlots";
import {
  CartQuantityValidationHeaders,
  CartQuantityValidationPayload,
  CartQuantityValidationResponse,
} from "../models/CartQuantityValidation";
import { store } from "./store";

export default class CartStore {
  cartItems: CartDetails | null = null;
  addedCartItems: AddToCartRes | null = null;
  deliverySlots: GetAllDeliverySlotsApiResponse | null = null;
  couponVerify: VerifyCouponApiResponse | null = null;
  cartQuantityValidation: CartQuantityValidationResponse | null = null;
  cartDeliveryTime: any = null;

  // Separate loaders
  loadingCart: boolean = false;
  loadingSlots: boolean = false;
  loadingClearCart: boolean = false;
  loadingCouponVerify: boolean = false;
  loadingExcelUpload: boolean = false;
  loadingCartValidation: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  /*------------------ Cart Loader ------------------*/
  getLoadingCart = () => this.loadingCart;

  setLoadingCart = (val: boolean) => {
    this.loadingCart = val;
  };

  /*------------------ Clear Cart Loader ------------------*/
  getLoadingClearCart = () => this.loadingClearCart;

  setLoadingClearCart = (val: boolean) => {
    this.loadingClearCart = val;
  };

  /*------------------ Coupon Verify Loader ------------------*/
  getLoadingCouponVerify = () => this.loadingCouponVerify;
  setLoadingCouponVerify = (val: boolean) => {
    this.loadingCouponVerify = val;
  };

  /*------------------ Slots Loader ------------------*/
  getLoadingSlots = () => this.loadingSlots;
  setLoadingSlots = (val: boolean) => {
    this.loadingSlots = val;
  };

  /*------------------ Excel Upload Loader ------------------*/
  getLoadingExcelUpload = () => this.loadingExcelUpload;
  setLoadingExcelUpload = (val: boolean) => {
    this.loadingExcelUpload = val;
  };

  /*------------------ Cart Quantity Validation Loader ------------------*/
  getLoadingCartValidation = () => this.loadingCartValidation;
  setLoadingCartValidation = (val: boolean) => {
    this.loadingCartValidation = val;
  };

  /*------------------ Cart Actions ------------------*/
  getCartItems = async (payload: CartDetailReqParams) => {
    this.setLoadingCart(true);
    try {
      const params = new URLSearchParams();
      params.append("outletId", String(payload.outletId));
      params.append("businessTypeId", String(payload.businessTypeId));
      params.append("warehouseId", String(store.commonStore.warehouseId ?? payload.warehouseId));
      params.append("order_type", String(payload.order_type));
      if (store.commonStore.isDirectDelivery !== null) {
        params.append("is_direct_delivery", store.commonStore.isDirectDelivery ? "1" : "0");
      }
      // Add couponId if it exists
      if (payload.couponId) {
        params.append("couponId", String(payload.couponId));
      }
      const result = await agent.Cart.detail(params);
      runInAction(() => {
        this.cartItems = result;
      });
      const finalWarehouseId = store.commonStore.warehouseId ?? payload.warehouseId;
      if (finalWarehouseId && payload.order_type && !store.commonStore.isDirectDelivery) {
        await this.postCartDeliveryTime(
          {
            warehouse_id: Number(finalWarehouseId),
            order_delivery_type: Number(payload.order_type),
          },
          {
            outletId: Number(payload.outletId),
          },
        );
      } else {
        runInAction(() => {
          this.cartDeliveryTime = null;
        });
      }
    } catch (error) {
      console.error("Error fetching cart items:", error);
    } finally {
      this.setLoadingCart(false);
    }
  };

  addTocart = async (payload: AddToCartReqParams) => {
    this.setLoadingCart(true);
    try {
      // Use warehouseId from getWarehouseId API
      const modifiedPayload = {
        ...payload,
        warehouseId: store.commonStore.warehouseId ?? payload.warehouseId,
        is_direct_delivery: store.commonStore.isDirectDelivery ? 1 : 0,
      };
      const result = await agent.Cart.addItem(modifiedPayload);
      runInAction(() => {
        this.addedCartItems = result;
      });

      // Fetch latest cart items
      await this.getCartItems({
        businessTypeId: payload.businessTypeId,
        warehouseId: store.commonStore.warehouseId ?? payload.warehouseId,
        order_type: payload.order_delivery_type,
        outletId: payload.outletId,
      });
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      this.setLoadingCart(false);
    }
  };

  validateCartQuantity = async (
    payload: CartQuantityValidationPayload,
    headers: CartQuantityValidationHeaders,
  ) => {
    this.setLoadingCartValidation(true);
    try {
      const effectivePayload = {
        ...payload,
        warehouseId: store.commonStore.warehouseId ?? payload.warehouseId,
        is_direct_delivery: store.commonStore.isDirectDelivery ? 1 : 0,
      };
      const result = await agent.Cart.validateQuantity(effectivePayload, headers);
      runInAction(() => {
        this.cartQuantityValidation = result;
      });
      return result;
    } catch (error) {
      console.error("Error validating cart quantity:", error);
      runInAction(() => {
        this.cartQuantityValidation = null;
      });
    } finally {
      this.setLoadingCartValidation(false);
    }
  };

  deleteCart = async (
    payload: ClearCartReqParams,
    headers: { outletId: number }
  ) => {
    this.setLoadingClearCart(true);
    try {
      const modifiedPayload = {
        ...payload,
        warehouseId: store.commonStore.warehouseId ?? payload.warehouseId,
        is_direct_delivery: store.commonStore.isDirectDelivery ? 1 : 0,
      };
      const response = await agent.Cart.clearCart(modifiedPayload, headers);
      runInAction(() => {
        if (response?.status_code === 1) {
          this.cartItems = null;
        }
      });
      return response;
    } catch (error) {
      console.error("Error in clear cart:", error);
    } finally {
      this.setLoadingClearCart(false);
    }
  };

  verifyCoupon = async (
    params: { couponId?: number | string; couponCode?: string; deliveryType: number; warehouseId: number },
    headers: { customerTypeId: number; outletId: number }
  ) => {
    this.setLoadingCouponVerify(true);
    try {
      const queryParams = new URLSearchParams();
      if (params.couponCode) {
        queryParams.append("couponCode", params.couponCode);
      } else if (params.couponId) {
        queryParams.append("couponId", String(params.couponId));
      }
      queryParams.append("deliveryType", String(params.deliveryType));
      queryParams.append(
        "warehouseId",
        String(store.commonStore.warehouseId ?? params.warehouseId)
      );
      const result = await agent.Cart.verifyCoupon(queryParams, headers);
      runInAction(() => {
        this.couponVerify = result;
      });
      return result;
    } catch (error) {
      console.error("Error verifying coupon:", error);
    } finally {
      this.setLoadingCouponVerify(false);
    }
  };

  /*------------------ Delivery Slots ------------------*/
  getAllDeliverySlots = async (
    orderDeliveryType: number,
    customerTypeId: number,
    outletId: number,
    customerOrgId: number,
  ) => {
    this.setLoadingSlots(true);
    try {
      const params = new URLSearchParams();
      params.append("orderDeliveryType", String(orderDeliveryType));
      const result = await agent.Cart.fetchDeliverySlots(params, {
        customerTypeId,
        outletId,
        customerOrgId,
      });
      const sorted = result.data
        .filter((slot) => slot.isActive)
        .sort((a, b) => a.rank - b.rank);
      runInAction(() => {
        this.deliverySlots = {
          ...result,
          data: sorted,
        };
      });
    } catch (error) {
      console.error("Error fetching delivery slots:", error);
    } finally {
      this.setLoadingSlots(false);
    }
  };

  /*------------------ Excel Upload ------------------*/
  uploadExcel = async (
    file: File,
    warehouseId: number,
    businessTypeId: number,
    outletId: number
  ): Promise<ExcelUploadResponse> => {
    this.setLoadingExcelUpload(true);
    try {
      const effectiveWarehouseId = store.commonStore.warehouseId ?? warehouseId;
      const result = await agent.Cart.uploadExcel(
        file,
        effectiveWarehouseId,
        businessTypeId,
        outletId
      );
      return result;
    } catch (error) {
      console.error("Error uploading excel:", error);
      throw error;
    } finally {
      this.setLoadingExcelUpload(false);
    }
  };

  /*------------------ Cart Delivery Time ------------------*/
  postCartDeliveryTime = async (
    payload: { warehouse_id: number; order_delivery_type: number },
    headers: { outletId: number },
  ) => {
    try {
      const result = await agent.Cart.postCartDeliveryTime(payload, headers);
      runInAction(() => {
        if (result?.status_code === 1) {
          this.cartDeliveryTime = result?.data;
        }
      });
      return result;
    } catch (error) {
      console.error("Error checking cart delivery time:", error);
    }
  };

  /*------------------ Helper Methods ------------------*/

  // Get slot by ID
  getSlotById = (slotId: string | number) => {
    if (!this.deliverySlots?.data) return null;
    return this.deliverySlots.data.find(
      (slot) => String(slot.id) === String(slotId)
    );
  };

  // Formatted slot time by ID
  getSlotTimeById = (slotId: string | number) => {
    const slot = this.getSlotById(slotId);
    if (!slot) return null;
    return `${slot.fromTime} - ${slot.toTime}`;
  };

  /*------------------ Computed ------------------*/
  get cartCount() {
    return this.cartItems?.data?.cart_items?.length ?? 0;
  }
}